/**
 * Regra única de Parcelas de Adiantamento. (Suporte 77951)
 *
 * O banco guarda uma parcela mista como DUAS linhas com o mesmo número de
 * `parcela`: origem 1 (Fundação) e origem 5 (Parceiro Estratégico). O total da
 * parcela é a SOMA das duas. Existe ainda o formato legado `origem = 0` (mista
 * consolidada numa linha só), que não deve mais ser gravado — o backend
 * (ProjetoController::atualizaParcelasAdiantamento) rejeita.
 *
 * Este mixin é a fonte única dessas duas conversões:
 *
 *   consolidarParcelasAdiantamento() -> formato de EXIBIÇÃO
 *     uma linha por número de parcela; mista vira origem 0 com valor_fbb /
 *     valor_parc / id_fbb / id_parc e `valor` = soma.
 *
 *   expandirParcelasAdiantamento()   -> formato de PERSISTÊNCIA
 *     desfaz a consolidação, devolvendo o formato canônico do banco e
 *     removendo os campos transitórios que o backend descartaria.
 *
 * As duas são inversas. Toda tela que lê, exibe ou grava parcela deve passar
 * por aqui — antes existiam quatro implementações divergentes (o componente
 * parcela-adiantamento, o Plano de Trabalho, o questionário de riscos e o
 * ParcelasDoProjeto no backend).
 *
 * Registrado como mixin global em main.js: os métodos ficam disponíveis em
 * qualquer template sem import.
 */

import _ from 'lodash'

const ORIGEM_MISTA = '0'
const ORIGEM_FBB = '1'
const ORIGEM_CONTRAPARTIDA = '2'
const ORIGEM_PARCEIRO = '5'

// Campos que só existem na visão consolidada e nunca vão para o banco
const CAMPOS_TRANSITORIOS = ['valor_fbb', 'valor_parc', 'id_fbb', 'id_parc', '_legadoSemBreakdown', '_origens']

/**
 * `origem` guarda o código de fonte do ERP e só o PRIMEIRO dígito identifica a
 * fonte — mesma convenção de ExtratoCronogramaRepository e ParcelasDoProjeto no
 * backend. Aceita também os rótulos textuais usados em partes do cadastro.
 */
function codigoOrigem (valor) {
  if (_.isNil(valor) || valor === '') return ''
  const texto = ('' + valor).trim()
  if (texto === 'FUNDACAO') return ORIGEM_FBB
  if (texto === 'CONTRAPARTIDA') return ORIGEM_CONTRAPARTIDA
  if (texto === 'PARCEIRO_ESTRATEGICO') return ORIGEM_PARCEIRO
  if (texto === 'MISTO' || texto === 'MISTA') return ORIGEM_MISTA
  return texto.substring(0, 1)
}

function numeroSeguro (valor) {
  const n = _.toNumber(valor)
  return _.isFinite(n) ? n : 0
}

// Soma em centavos inteiros para não acumular drift de ponto flutuante
function somaValores (lista, campo) {
  const centavos = _.reduce(lista, function (acc, item) {
    return acc + Math.round(numeroSeguro(_.get(item, campo, 0)) * 100)
  }, 0)
  return centavos / 100
}

export const parcelasAdiantamento = {
  methods: {
    /**
     * Rótulo oficial da origem de uma parcela. Único lugar onde esses textos
     * são escritos: "Parcela Mista" e "Parceiro Estratégico" são os termos
     * acordados com a área.
     */
    rotuloOrigemParcela (valor) {
      switch (codigoOrigem(valor)) {
        case ORIGEM_MISTA: return 'Parcela Mista'
        case ORIGEM_FBB: return 'Fundação'
        case ORIGEM_CONTRAPARTIDA: return 'Contrapartida'
        case ORIGEM_PARCEIRO: return 'Parceiro Estratégico'
        case '': return '⚠️ Origem não informada'
        default: return '⚠️ Origem inválida (' + valor + ')'
      }
    },

    ehParcelaMista (parcela) {
      return codigoOrigem(_.get(parcela, 'origem')) === ORIGEM_MISTA
    },

    /**
     * Há alguma parcela mista na lista já consolidada? As telas usam isso para
     * decidir se exibem as colunas de desdobramento por origem — projeto sem
     * recurso misto mostra só a coluna de valor.
     */
    haParcelaMista (parcelas) {
      const vm = this
      return _.some(parcelas || [], function (p) { return vm.ehParcelaMista(p) })
    },

    /**
     * Total de uma lista de parcelas, em qualquer um dos dois formatos. Sem o
     * segundo argumento soma o valor da parcela; com 'valor_fbb' ou 'valor_parc'
     * soma só a parte daquela origem (útil para o rodapé das telas que exibem o
     * desdobramento).
     */
    totalParcelasAdiantamento (parcelas, campo) {
      return somaValores(parcelas || [], campo || 'valor')
    },

    /**
     * Quantidade real de parcelas: conta números distintos, para que a mista
     * (duas linhas) não seja contada duas vezes. Linhas sem número contam uma
     * a uma, porque não há como agrupá-las.
     */
    quantidadeParcelasAdiantamento (parcelas) {
      const validas = _.filter(parcelas || [], function (p) { return !_.isNil(p) })
      const comNumero = _.filter(validas, function (p) {
        return !_.isNil(_.get(p, 'parcela')) && _.get(p, 'parcela') !== ''
      })
      const numerosDistintos = _.uniq(_.map(comNumero, function (p) { return '' + p.parcela })).length
      return numerosDistintos + (validas.length - comNumero.length)
    },

    /**
     * Formato do banco -> formato de exibição. Agrupa pelo número da parcela;
     * linhas sem número não são agrupadas (seriam todas fundidas numa só) e
     * seguem individuais.
     */
    consolidarParcelasAdiantamento (parcelas) {
      if (!_.isArray(parcelas) || parcelas.length === 0) return []
      const vm = this
      const validas = _.filter(parcelas, function (p) { return !_.isNil(p) })
      const comNumero = _.filter(validas, function (p) {
        return !_.isNil(_.get(p, 'parcela')) && _.get(p, 'parcela') !== ''
      })
      const semNumero = _.difference(validas, comNumero)
      const grupos = _.groupBy(comNumero, function (p) { return '' + p.parcela })
      const consolidadas = _.flatten(_.map(grupos, function (grupo) {
        return vm.consolidarGrupoParcelas(grupo)
      }))
      const soltas = _.map(semNumero, function (p) {
        return vm.normalizarParcelaSimples(_.cloneDeep(p))
      })
      return _.orderBy(_.concat(consolidadas, soltas), [function (p) {
        return numeroSeguro(_.get(p, 'parcela'))
      }], ['asc'])
    },

    /**
     * Formato de exibição -> formato do banco. Mista vira duas linhas (origem 1
     * e origem 5) com o mesmo número de parcela; as demais passam direto, sem
     * os campos transitórios. Nunca devolve origem 0.
     */
    expandirParcelasAdiantamento (parcelas) {
      return _.flatMap(parcelas || [], function (p) {
        if (codigoOrigem(_.get(p, 'origem')) !== ORIGEM_MISTA) {
          return [_.omit(_.cloneDeep(p), CAMPOS_TRANSITORIOS)]
        }
        // Mista legada: sem desdobramento não há como expandir, e expandir com zero
        // destruiria o valor da parcela em silêncio. Devolve a linha como está —
        // a tela deve barrar pela restrição e, se escapar, o backend rejeita a
        // origem 0 em alto e bom som. Nunca perder o valor por conta própria.
        if (_.get(p, '_legadoSemBreakdown') === true) {
          return [_.omit(_.cloneDeep(p), _.without(CAMPOS_TRANSITORIOS, '_legadoSemBreakdown'))]
        }
        const base = _.omit(_.cloneDeep(p), _.concat(CAMPOS_TRANSITORIOS, ['id', 'origem', 'valor']))
        const monta = function (origem, valor, id) {
          const linha = _.cloneDeep(base)
          linha.origem = origem
          linha.valor = valor
          if (!_.isNil(id)) linha.id = id
          return linha
        }
        return [
          monta(1, numeroSeguro(_.get(p, 'valor_fbb', 0)), _.get(p, 'id_fbb', null)),
          monta(5, numeroSeguro(_.get(p, 'valor_parc', 0)), _.get(p, 'id_parc', null))
        ]
      })
    },

    /**
     * Parcela mista legada (origem 0 sem desdobramento) não pode ser expandida
     * sem perder o valor: as duas linhas sairiam zeradas. As telas usam isso
     * para exigir correção manual antes de gravar.
     */
    parcelasLegadasSemDesdobramento (parcelas) {
      return _.filter(parcelas || [], ['_legadoSemBreakdown', true])
    },

    /**
     * Mescla um grupo de linhas com o mesmo número de parcela. Devolve a linha
     * consolidada quando o grupo tem as duas origens, ou a lista normalizada
     * quando tem só uma.
     */
    consolidarGrupoParcelas (grupo) {
      const vm = this
      const porOrigem = function (codigo) {
        return _.filter(grupo, function (p) { return codigoOrigem(_.get(p, 'origem')) === codigo })
      }
      const linhasFbb = porOrigem(ORIGEM_FBB)
      const linhasParc = porOrigem(ORIGEM_PARCEIRO)
      const linhasMistas = porOrigem(ORIGEM_MISTA)
      // Mista legada (sem desdobramento) não entra na mesclagem por origem
      const mistasComValores = _.filter(linhasMistas, function (p) {
        return !_.isNil(_.get(p, 'valor_fbb')) || !_.isNil(_.get(p, 'valor_parc'))
      })
      const mistasLegadas = _.difference(linhasMistas, mistasComValores)
      // Uma linha origem 0 que referencia as canônicas do próprio grupo é
      // redundante: contaria o valor em dobro
      const idsCanonicos = _.compact(_.map(_.union(linhasFbb, linhasParc), 'id'))
      const mistasNovas = _.filter(mistasComValores, function (m) {
        const refs = _.compact([_.get(m, 'id', null), _.get(m, 'id_fbb', null), _.get(m, 'id_parc', null)])
        return _.intersection(refs, idsCanonicos).length === 0
      })
      const temFbb = linhasFbb.length > 0 || _.some(mistasNovas, function (m) { return !_.isNil(_.get(m, 'valor_fbb')) })
      const temParc = linhasParc.length > 0 || _.some(mistasNovas, function (m) { return !_.isNil(_.get(m, 'valor_parc')) })
      if (!temFbb || !temParc) {
        // Origem única no grupo: mantém as linhas individuais normalizadas
        return _.map(_.union(linhasFbb, linhasParc, mistasNovas, mistasLegadas), function (p) {
          return vm.normalizarParcelaSimples(_.cloneDeep(p))
        })
      }
      const parcela = _.cloneDeep(_.head(_.union(linhasFbb, linhasParc, mistasNovas)))
      const valorFbb = somaValores(linhasFbb, 'valor') + somaValores(mistasNovas, 'valor_fbb')
      const valorParc = somaValores(linhasParc, 'valor') + somaValores(mistasNovas, 'valor_parc')
      _.set(parcela, 'origem', 0)
      _.set(parcela, 'valor_fbb', valorFbb)
      _.set(parcela, 'valor_parc', valorParc)
      // Rastreio dos ids originais: a linha canônica tem prioridade sobre a mista
      _.set(parcela, 'id_fbb', _.get(_.head(linhasFbb), 'id', _.get(_.head(mistasNovas), 'id_fbb', null)))
      _.set(parcela, 'id_parc', _.get(_.head(linhasParc), 'id', _.get(_.head(mistasNovas), 'id_parc', null)))
      _.set(parcela, 'valor', Math.round((valorFbb + valorParc) * 100) / 100)
      // Mista legada do mesmo número segue individual, para não sumir da tela
      const legadas = _.map(mistasLegadas, function (p) {
        return vm.normalizarParcelaSimples(_.cloneDeep(p))
      })
      return _.concat([parcela], legadas)
    },

    /**
     * Preenche valor_fbb / valor_parc / valor de uma linha que não foi mesclada,
     * para que a grade tenha sempre os mesmos campos.
     */
    normalizarParcelaSimples (p) {
      const codigo = codigoOrigem(_.get(p, 'origem'))
      const valor = numeroSeguro(_.get(p, 'valor', 0))
      if (codigo === ORIGEM_MISTA) {
        const temFbb = !_.isNil(_.get(p, 'valor_fbb'))
        const temParc = !_.isNil(_.get(p, 'valor_parc'))
        if (!temFbb && !temParc) {
          // Mista legada sem desdobramento: preserva o valor e sinaliza para
          // correção manual. Não dividir 50/50 e não zerar.
          _.set(p, 'valor_fbb', null)
          _.set(p, 'valor_parc', null)
          _.set(p, '_legadoSemBreakdown', true)
          _.set(p, 'valor', valor)
          return p
        }
        const valorFbb = numeroSeguro(_.get(p, 'valor_fbb', 0))
        const valorParc = numeroSeguro(_.get(p, 'valor_parc', 0))
        _.set(p, 'valor_fbb', valorFbb)
        _.set(p, 'valor_parc', valorParc)
        _.set(p, 'valor', Math.round((valorFbb + valorParc) * 100) / 100)
        return p
      }
      // Origem única: o valor vai para a coluna correspondente e a outra fica vazia
      _.set(p, 'valor_fbb', codigo === ORIGEM_FBB ? valor : null)
      _.set(p, 'valor_parc', codigo === ORIGEM_PARCEIRO ? valor : null)
      _.set(p, 'valor', valor)
      return p
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/parcelasAdiantamento.js